import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { MuiEditableDataGrid, MuiEditableDataGridExternalProps } from "./muiEditableDataGrid";
import {
    BulkActionBar,
    BulkEditField,
} from "./bulkActionBar";
import { showApiError, showApiSuccess } from "../formUtils";
import { useTranslation } from "react-i18next";
import * as store from "../../../framework/customStore";
import * as storeActions from "../../../models/store/storeActions";
import { ConfirmationDialogType } from "../../../models/store/storeTypes";

// --- Context for checkbox state ---

interface BulkSelectionContextValue {
    selectedIds: string[];
    toggle: (id: string, checked: boolean) => void;
    selectAll: (checked: boolean) => void;
    allSelected: boolean;
    someSelected: boolean;
    isEditing: boolean;
    hasSelectableRows: boolean;
}

const BulkSelectionContext = createContext<BulkSelectionContextValue | null>(null);

const useBulkSelection = () => {
    const ctx = useContext(BulkSelectionContext);
    if (!ctx) throw new Error("useBulkSelection must be used within BulkEditableDataGrid");
    return ctx;
};

// --- Checkbox components that consume context ---

function BulkSelectHeaderCheckbox() {
    const { allSelected, someSelected, selectAll, isEditing, hasSelectableRows } = useBulkSelection();
    if (!hasSelectableRows) return null;
    const checked = allSelected;
    return (
        <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onClick={() => selectAll(!checked)}
            disabled={isEditing}
            sx={{ p: 0 }}
        />
    );
}

function BulkSelectCellCheckbox<T extends { id?: string }>({
    row,
    isBulkSelectable,
}: {
    row: T;
    isBulkSelectable?: (row: T) => boolean;
}) {
    const { selectedIds, toggle, isEditing } = useBulkSelection();
    const selectable = !isBulkSelectable || isBulkSelectable(row);
    if (!selectable) return null;

    const checked = selectedIds.includes(row.id);
    return (
        <Checkbox
            size="small"
            checked={checked}
            onClick={(e) => { e.stopPropagation(); toggle(row.id, !checked); }}
            disabled={isEditing}
            sx={{ p: 0 }}
        />
    );
}

// --- Main component ---

interface BulkProps<T> {
    bulkEditFields: BulkEditField[];
    isBulkSelectable?: (row: T) => boolean;
    onBulkDelete: (ids: string[]) => Promise<void>;
    onBulkEditApply: (ids: string[], fieldKey: string, value: string | null) => Promise<void>;
    deleteConfirmationKey?: string;
    onBulkSuccess?: () => void;
}

type BulkEditableDataGridProps<T> = BulkProps<T> & MuiEditableDataGridExternalProps<T>;

export function BulkEditableDataGrid<T extends { id?: string }>({
    bulkEditFields,
    isBulkSelectable,
    onBulkDelete,
    onBulkEditApply,
    deleteConfirmationKey,
    onBulkSuccess,
    rows,
    columns,
    editId,
    disableAddRowButton,
    isCellEditable,
    ...props
}: BulkEditableDataGridProps<T>) {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const isEditing = !!editId;
    const hasSelection = selectedIds.length > 0;

    const clearSelection = useCallback(() => {
        setSelectedIds([]);
    }, []);

    const toggle = useCallback((id: string, checked: boolean) => {
        setSelectedIds((prev) =>
            checked ? [...prev, id] : prev.filter((i) => i !== id)
        );
    }, []);

    const selectAll = useCallback((checked: boolean) => {
        setSelectedIds(checked
            ? rows.filter((r) => !isBulkSelectable || isBulkSelectable(r)).map((r) => r.id)
            : []
        );
    }, [rows, isBulkSelectable]);

    const executeDelete = useCallback(async() => {
        setSaving(true);
        try {
            await onBulkDelete(selectedIds);
            showApiSuccess(t("common.deleteSuccess"));
            clearSelection();
            onBulkSuccess?.();
        } catch (error) {
            showApiError(error);
        } finally {
            setSaving(false);
        }
    }, [selectedIds, onBulkDelete, clearSelection, onBulkSuccess, t]);

    const handleDelete = useCallback(() => {
        if (selectedIds.length === 0) return;
        if (deleteConfirmationKey) {
            store.customStore.dispatch(
                storeActions.setConfirmation(
                    ConfirmationDialogType.Warning,
                    t("common.warning"),
                    t(deleteConfirmationKey, { count: selectedIds.length }),
                    () => void executeDelete(),
                    () => store.customStore.dispatch(storeActions.clearConfirmation())
                )
            );
        } else {
            void executeDelete();
        }
    }, [selectedIds, deleteConfirmationKey, executeDelete, t]);

    const handleEditApply = useCallback(async(fieldKey: string, value: string | null) => {
        if (selectedIds.length === 0) return;
        setSaving(true);
        try {
            await onBulkEditApply(selectedIds, fieldKey, value);
            showApiSuccess(t("common.saveSuccess"));
            clearSelection();
            onBulkSuccess?.();
        } catch (error) {
            showApiError(error);
        } finally {
            setSaving(false);
        }
    }, [selectedIds, onBulkEditApply, clearSelection, onBulkSuccess, t]);

    const selectableRows = useMemo(
        () => rows.filter((r) => !isBulkSelectable || isBulkSelectable(r)),
        [rows, isBulkSelectable]
    );

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allSelected = useMemo(
        () =>
            selectableRows.length > 0 &&
            selectableRows.every((r) => selectedIdSet.has(r.id)),
        [selectableRows, selectedIdSet]
    );
    const someSelected = !allSelected && selectedIds.length > 0;

    const hasSelectableRows = selectableRows.length > 0;

    const contextValue = useMemo<BulkSelectionContextValue>(() => ({
        selectedIds,
        toggle,
        selectAll,
        allSelected,
        someSelected,
        isEditing,
        hasSelectableRows,
    }), [selectedIds, toggle, selectAll, allSelected, someSelected, isEditing, hasSelectableRows]);

    // Stable column definition — no selection-related deps
    const checkboxColumn = useMemo<GridColDef>(() => ({
        field: "__bulk_select__",
        headerName: "",
        width: 42,
        minWidth: 42,
        maxWidth: 42,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        hideable: false,
        resizable: false,
        disableReorder: true,
        pinned: "left",
        renderHeader: () => <BulkSelectHeaderCheckbox />,
        renderCell: (params: GridRenderCellParams<T>) => (
            <BulkSelectCellCheckbox row={params.row} isBulkSelectable={isBulkSelectable} />
        ),
    }), [isBulkSelectable]);

    const columnsWithCheckbox = useMemo(
        () => [checkboxColumn, ...columns],
        [checkboxColumn, columns]
    );

    return (
        <BulkSelectionContext.Provider value={contextValue}>
            <MuiEditableDataGrid
                rows={rows}
                columns={columnsWithCheckbox}
                editId={editId}
                disableAddRowButton={disableAddRowButton || hasSelection}
                isCellEditable={hasSelection ? () => false : isCellEditable}
                {...props}
            />
            {hasSelection && (
                <BulkActionBar
                    selectedCount={selectedIds.length}
                    fields={bulkEditFields}
                    saving={saving}
                    onDelete={handleDelete}
                    onEditApply={(fieldKey, value) => void handleEditApply(fieldKey, value)}
                    onClear={clearSelection}
                />
            )}
        </BulkSelectionContext.Provider>
    );
}
